import moment from "moment";

// Opta F24 event typeIds that count toward a player's heatmap; 44, 59 and 67
// only count when outcome is 0, per the StatsPerform heatmap spec.
const OPTA_HEATMAP_EVENT_TYPES = [
  1, 2, 3, 4, 7, 8, 10, 11, 12, 13, 14, 15, 16, 41, 42, 44, 45, 49, 50, 51, 52,
  53, 54, 55, 56, 59, 60, 61, 67, 69, 72, 73, 74,
];
const OPTA_HEATMAP_OUTCOME_ZERO_ONLY_TYPES = [44, 59, 67];

// Yield to the event loop every this many events so binning a full match's
// feed doesn't hold the main thread for the whole pass in one go.
const HEATMAP_YIELD_EVERY = 500;

export class PlayerHeatmap {
  constructor({ animate, preview, update, animateOff }) {
    this.onAir = false;
    this.sendAnimate = animate;
    this.sendAnimateOff = animateOff;
    this.sendPreview = preview;
    this.sendUpdate = update;
    this.name = "PlayerHeatmap";
    this.cb_scene = "Player Heatmap";
    this.ff_scene = "FF Player Heatmap";
    this.data = [];
    this.previewSeq = 0;
  }

  async preview({ options }) {
    // Local array, not this.data: buildOptaHeatmap awaits mid-build, so a
    // second preview() (rapid re-click) can start before this one finishes.
    // Writing straight to this.data would let the two calls interleave.
    const data = [];
    const token = ++this.previewSeq;
    this.onAir = false;
    if (options.full_frame) {
      this.scene = this.ff_scene;
    } else {
      this.scene = this.cb_scene;
    }
    if (options.type) {
      this.scene = this.scene + " " + options.type;
    }
    data.push({
      name: "Competition",
      value: { text: options.game?.competition?.name || "" },
    });
    data.push({
      name: "Competition Colour",
      value: { colour: options.game?.competition?.colour || "" },
    });
    data.push({
      name: "Competition Badge",
      value: { image: "${user}" + options.game?.competition?.badge || "" },
    });

    data.push({
      name: "Title",
      value: {
        text: options.player.title || "",
      },
    });
    data.push({
      name: "Shirt Number",
      value: {
        text:
          options.player.shirt_number ?? options.player.opta_shirt_number ?? "",
      },
    });

    data.push({
      name: "Position",
      value: {
        text: options.player?.squad?.position ?? "",
      },
    });

    data.push({
      name: "Image",
      value: {
        image: "${user}" + options?.player?.image,
      },
    });

    data.push({
      name: "First Name",
      value: {
        text: options.player.first_name ?? options.player.opta_first_name ?? "",
      },
    });
    data.push({
      name: "Last Name",
      value: {
        text: options.player.last_name ?? options.player.opta_last_name ?? "",
      },
    });
    data.push({
      name: "First Name Uppercase",
      value: {
        text:
          options.player.first_name_upper ??
          options.player.opta_first_name?.toUpperCase() ??
          "",
      },
    });
    data.push({
      name: "Last Name Uppercase",
      value: {
        text:
          options.player.last_name_upper ??
          options.player.opta_last_name?.toUpperCase() ??
          "",
      },
    });
    data.push({
      name: "Full Name",
      value: {
        text:
          (options.player.first_name ?? options.player.opta_first_name ?? "") +
          " " +
          (options.player.last_name ?? options.player.opta_last_name ?? ""),
      },
    });
    data.push({
      name: "Full Name Uppercase",
      value: {
        text:
          (options.player.first_name_upper ??
            options.player.opta_first_name?.toUpperCase() ??
            "") +
          " " +
          (options.player.last_name_upper ??
            options.player.opta_last_name?.toUpperCase() ??
            ""),
      },
    });

    data.push({
      name: "Nationality",
      value: {
        text:
          options?.player?.nationality?.code ??
          options?.player?.nationality?.opta_code,
      },
    });

    data.push({
      name: "Subtitle",
      value: {
        text: "",
      },
    });
    data.push({
      name: "Team Name",
      value: {
        text: options.team?.name || "",
      },
    });

    data.push({
      name: "Goal Time",
      value: {
        text: "",
      },
    });

    data.push({
      name: "Team Badge",
      value: {
        image: "${user}" + options.team?.variables?.badge || "",
      },
    });
    data.push({
      name: "Team Secondary Badge",
      value: {
        image: "${user}" + options.team?.variables?.alt_badge || "",
      },
    });

    if (options?.game?.home_team?._id === options.team?._id) {
      data.push({
        name: "Team Type",
        value: {
          text: "home",
        },
      });
      data.push({
        name: "Team Kit BG Colour",
        value: {
          colour:
            options.team?.variables?.[
              options.game?.home_team_kit + "_kit_bg"
            ] || "",
        },
      });
      data.push({
        name: "Team Kit Text Colour",
        value: {
          colour:
            options.team?.variables?.[
              options.game?.home_team_kit + "_kit_text"
            ] || "",
        },
      });
      if (options?.game?.opta_ID) {
        await this.buildOptaHeatmap({
          events: options?.opta_events,
          player: options?.player,
          data,
        });
      } else if (options?.game?.dfb_ID) {
        this.buildDFBHeatmap({
          stats: options?.game?.sportec_player_stats?.home?.find(
            (p) => p?.player === options?.player?.dfb_ID,
          ),
          data,
        });
      }
    } else {
      data.push({
        name: "Team Type",
        value: {
          text: "away",
        },
      });
      data.push({
        name: "Team Kit BG Colour",
        value: {
          colour:
            options.team?.variables?.[
              options.game?.away_team_kit + "_kit_bg"
            ] || "",
        },
      });
      data.push({
        name: "Team Kit Text Colour",
        value: {
          colour:
            options.team?.variables?.[
              options.game?.away_team_kit + "_kit_text"
            ] || "",
        },
      });
      if (options?.game?.opta_ID) {
        await this.buildOptaHeatmap({
          events: options?.opta_events,
          player: options?.player,
          data,
        });
      } else if (options?.game?.dfb_ID) {
        this.buildDFBHeatmap({
          stats: options?.game?.sportec_player_stats?.away?.find(
            (p) => p?.player === options?.player?.dfb_ID,
          ),
          data,
        });
      }
    }

    // A newer preview() call has already superseded this one - drop this
    // stale result instead of clobbering this.data or re-sending it.
    if (token !== this.previewSeq) return;
    this.data = data;
    this.sendPreview(this.scene, 500, this.data, "IN");
  }

  async buildOptaHeatmap({ events, player, data }) {
    // Opta x/y arrive as 0-100 pitch percentages, so bin against that range,
    // but the Heat Map template renders directly onto the same 294x172
    // canvas buildDFBHeatmap uses - output cell centers in canvas units too,
    // or the data all lands crushed into one corner of the pitch graphic.
    const width = 100;
    const height = 100;
    const canvasWidth = 294;
    const canvasHeight = 172;
    const cols = 40;
    const rows = 38;

    const stepX = width / cols;
    const stepY = height / rows;
    const cellWidth = canvasWidth / cols;
    const cellHeight = canvasHeight / rows;

    const grid = new Array(cols * rows).fill(0);
    const source = events ?? [];

    for (let i = 0; i < source.length; i++) {
      const e = source[i];
      const isCandidate =
        e?.playerId === player?.opta_ID &&
        Number.isFinite(e?.x) &&
        Number.isFinite(e?.y) &&
        OPTA_HEATMAP_EVENT_TYPES.includes(e?.typeId) &&
        (!OPTA_HEATMAP_OUTCOME_ZERO_ONLY_TYPES.includes(e?.typeId) ||
          e?.outcome === 0);

      if (isCandidate) {
        // Mirror y: Opta's y runs opposite to the flank orientation the
        // DFB-calibrated template expects, which was swapping LB/RB sides.
        const col = Math.min(cols - 1, Math.max(0, Math.floor(e.x / stepX)));
        const row = Math.min(
          rows - 1,
          Math.max(0, Math.floor((height - e.y) / stepY)),
        );
        grid[row * cols + col]++;
      }

      if (i > 0 && i % HEATMAP_YIELD_EVERY === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }

    const totalCount = grid.reduce((sum, c) => sum + c, 0);

    const heatExport = [];
    for (let i = 0; i < grid.length; i++) {
      const count = grid[i];
      if (count === 0) continue;
      const col = i % cols;
      const row = Math.floor(i / cols);

      const x = col * cellWidth + cellWidth / 2;
      const y = row * cellHeight + cellHeight / 2;
      // Share of this player's total qualifying events, not share of their
      // busiest cell - normalizing to the max forced every player's top cell
      // to full heat even when it wasn't a real hotspot, washing wide areas
      // red. Capped so one small-sample freak cluster can't blow past
      // buildDFBHeatmap's ~0-10 range.
      const intensity = totalCount
        ? Math.min(10, (count / totalCount) * 50)
        : 0;

      heatExport.push([x, y, intensity]);
    }
    data.push({
      name: "Heatmap",
      value: { text: JSON.stringify(heatExport) },
    });
  }

  buildDFBHeatmap({ team, stats, options, data }) {
    const width = 294;
    const height = 172;
    const cols = 20;
    const rows = 14;

    const stepX = width / cols;
    const stepY = height / rows;

    const heatExport = [];
    const heatImport = stats?.heatmap?.data

    for (let i = 0; i < heatImport.length; i++) {
      const val = parseInt(heatImport[i]);
      if (val === 0) continue;
      const col = i % cols;
      const row = Math.floor(i / cols);

      const x = col * stepX + (stepX / 2);
      const y = row * stepY + (stepY / 2);
      const intensity = val / 10;

      heatExport.push([x, y, intensity])
    }
    data.push({
      name: "Heatmap",
      value: { text: JSON.stringify(heatExport) }
    });
  }

  animate({ options }) {
    this.onAir = true;

    this.sendAnimate(this.scene, this.data);
  }
  animateOff({ options }) {
    this.onAir = false;
    this.sendAnimateOff(this.scene, []);
  }
}
